import { useEffect, useState, type ReactNode } from 'react';
import { ConfirmDialogProvider } from '../widget-ui/confirm-dialog';
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { BrandExpectNotice } from './brand-expect-notice';
import { EmbedGuestShowcaseHeroHeader } from './EmbedGuestShowcaseHeroHeader';
import { EmbedOrgHeroHeader } from './EmbedOrgHeroHeader';
import { EmbedWidgetTheme } from './EmbedWidgetTheme';
import { EmbedBootstrapProvider } from '../widget-bootstrap-provider';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { BlockLoader } from '../widget-ui/block-loader';
import { EmbedSiteFooter } from '../widget-ui/embed-site-footer';
import {
  BlueHeader,
  embedClientBlueHeaderFooterNavClass,
  embedClientBlueHeaderNavLinkClass
} from '../widget-ui/blue-header';
import { BookingShell } from './BookingShell';
import { MobileShell } from './MobileShell';
import { TrainerMobileShell } from './TrainerMobileShell';
import { useBranchLabels } from '../widget-hooks/useBranchLabels';

import { isTrainerStaffMe } from '../widget-lib/embed-route-policy';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { requiredClientModuleByPath } from '../widget-lib/embed-client-module-gates';
import { PATH } from '../widget-lib/path-routes';
import { clearEmbedSession, clearGlobalEmbedSession } from '../widget-lib/embed-session-store';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';

const navClass = ({ isActive }: { isActive: boolean }) =>
  isActive
    ? 'rounded-full bg-[color-mix(in_srgb,var(--accent)_12%,transparent)] px-3 py-1 text-[var(--accent)] font-semibold text-[13px]'
    : 'rounded-full px-3 py-1 text-[13px] text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text)] transition-colors';

function navModuleLinkVisible(path: string, modules: Record<string, boolean> | null | undefined): boolean {
  const req = requiredClientModuleByPath(path);
  if (req == null) return true;
  return modules?.[req] === true;
}

function AppHeader() {
  const [headerPhotoBroken, setHeaderPhotoBroken] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const {
    widgetConfig,
    globalWidgetConfig,
    accessToken,
    embedMode,
    widgetContour,
    effectiveModules,
    me,
    meLoading,
    canFetchWidget,
    guestShowcaseOrg,
    guestShowcaseBrowse,
    orgSlug,
    unpaidBookingsCount
  } = useEmbedBootstrap();
  const modulesForNav = effectiveModules ?? me?.modules ?? null;
  /** Мини-сайт клиентского виджета (`wn=client`): верхняя шапка с фото, без нижних табов — отличается от `wn=mobile`. */
  const clientMiniSite = widgetContour === 'client';
  /** Расписание без входа — подписанная ссылка, витрина глобального виджета или JWT. */
  const hasClientBrowseAccess = Boolean(accessToken || canFetchWidget || guestShowcaseBrowse);
  /** Гость: узкий просмотр (расписание, салоны) — студия по ссылке или выбор из витрины. */
  const anonymousWidgetBrowse = Boolean((canFetchWidget || guestShowcaseBrowse) && !accessToken);
  const showBranchesNav = Boolean((widgetConfig?.branches ?? []).length > 1);
  const showBranchesNavAnonymous =
    anonymousWidgetBrowse &&
    Boolean(
      (widgetConfig?.branches ?? []).length >= 1 ||
        (guestShowcaseOrg?.branches ?? []).length >= 1
    );
  const title =
    widgetConfig?.name?.trim() ||
    globalWidgetConfig?.title?.trim() ||
    'DomCRM';
  const headerPhotoUrl = displayableMediaUrl(widgetConfig?.photo_url);
  const showHeaderPhoto = headerPhotoUrl != null && !headerPhotoBroken;

  useEffect(() => {
    setHeaderPhotoBroken(false);
  }, [headerPhotoUrl]);
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const embedGlobal = searchParams.get('embed_global') === '1';
  /** На `/trainer/*` показываем тренерскую навигацию даже до входа (сессия ещё client). */
  const isTrainerShell =
    embedMode === 'trainer' ||
    location.pathname === PATH.trainer ||
    location.pathname.startsWith(`${PATH.trainer}/`);

  const showTrainerFullNav =
    isTrainerShell &&
    Boolean(accessToken) &&
    !meLoading &&
    me != null &&
    isTrainerStaffMe(me);

  const branchLabels = useBranchLabels();

  const navLinkClass = clientMiniSite ? embedClientBlueHeaderNavLinkClass : navClass;

  const useOrgHeroBanner = Boolean(widgetConfig && !clientMiniSite);
  const useGuestShowcaseHero = Boolean(!isTrainerShell && guestShowcaseOrg && !widgetConfig && !clientMiniSite);

  const isGlobalCatalog = embedGlobal && !orgSlug && !guestShowcaseOrg;

  const headerNav = (
    <nav
      className={clientMiniSite ? embedClientBlueHeaderFooterNavClass() : 'flex flex-wrap items-center gap-3 text-sm'}
      aria-label={clientMiniSite ? 'Разделы приложения' : undefined}
    >
      {isGlobalCatalog ? (
        accessToken ? (
          <>
            <NavLink to={`${PATH.account}${suffix}`} className={navLinkClass}>
              Личный кабинет
            </NavLink>
          </>
        ) : null
      ) : isTrainerShell ? (
        <>
          {showTrainerFullNav ? (
            <>
              <NavLink to={`${PATH.trainerProfile}${suffix}`} className={navLinkClass}>
                Профиль
              </NavLink>
              <NavLink to={`${PATH.trainerWorkSchedule}${suffix}`} className={navLinkClass}>
                График
              </NavLink>
              <NavLink to={`${PATH.trainerMySessions}${suffix}`} className={navLinkClass}>
                Мои тренировки
              </NavLink>
              <NavLink to={`${PATH.trainerBookClient}${suffix}`} className={navLinkClass}>
                Записать клиента
              </NavLink>
              <button
                type="button"
                className={typeof navLinkClass === 'function' ? navLinkClass({ isActive: false }) : navLinkClass}
                onClick={() => {
                  clearEmbedSession(orgSlug);
                  clearGlobalEmbedSession();
                }}
              >
                Выйти
              </button>
            </>
          ) : null}
          {!accessToken ? (
            <NavLink to={`${PATH.trainerSignIn}${suffix}`} className={navLinkClass}>
              Вход тренера
            </NavLink>
          ) : null}
        </>
      ) : (
        <>
          {embedGlobal ? (
            <button
              type="button"
              className={
                clientMiniSite
                  ? 'shrink-0 rounded-full px-3 py-2 text-[11px] font-semibold text-[var(--accent-fg)] transition-colors hover:bg-[var(--accent-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-fg)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text)] text-sm font-semibold'
              }
              onClick={() => {
                const p = new URLSearchParams();
                p.set('embed_global', '1');
                const parentOriginVal = searchParams.get('embed_parent_origin');
                if (parentOriginVal) p.set('embed_parent_origin', parentOriginVal);
                const wnVal = searchParams.get('wn');
                if (wnVal) p.set('wn', wnVal);
                const widgetChannelVal = searchParams.get('widget_channel');
                if (widgetChannelVal) p.set('widget_channel', widgetChannelVal);
                window.location.href = `/embed/?${p.toString()}`;
              }}
            >
              ← Каталог
            </button>
          ) : null}
          {!accessToken ? (
            <>
              <NavLink to={`${PATH.signIn}${suffix}`} className={navLinkClass}>
                Вход
              </NavLink>
              <NavLink to={`${PATH.register}${suffix}`} className={navLinkClass}>
                Регистрация
              </NavLink>
            </>
          ) : null}
          {hasClientBrowseAccess ? (
            anonymousWidgetBrowse ? (
              <>
                <NavLink to={`${PATH.schedule}${suffix}`} className={navLinkClass}>
                  Расписание
                </NavLink>
                <NavLink to={`${PATH.trainers}${suffix}`} className={navLinkClass}>
                  Тренеры
                </NavLink>
                <NavLink to={`${PATH.services}${suffix}`} className={navLinkClass}>
                  Услуги
                </NavLink>
                {showBranchesNavAnonymous ? (
                  <NavLink to={`${PATH.branches}${suffix}`} className={navLinkClass}>
                    {branchLabels.plural}
                  </NavLink>
                ) : null}
              </>
            ) : (
              <>
                <NavLink to={`${PATH.schedule}${suffix}`} className={navLinkClass}>
                  Расписание
                </NavLink>
                {Boolean(accessToken && navModuleLinkVisible(PATH.myAccess, modulesForNav)) && (
                  <NavLink to={`${PATH.myAccess}${suffix}`} className={navLinkClass}>
                    QR-коды
                  </NavLink>
                )}
                {(!accessToken || navModuleLinkVisible(PATH.trainers, modulesForNav)) && (
                <NavLink to={`${PATH.trainers}${suffix}`} className={navLinkClass}>
                  Тренеры
                </NavLink>
                )}
                {(!accessToken || navModuleLinkVisible(PATH.services, modulesForNav)) && (
                <NavLink to={`${PATH.services}${suffix}`} className={navLinkClass}>
                  Услуги
                </NavLink>
                )}
                {showBranchesNav ? (
                  <NavLink to={`${PATH.branches}${suffix}`} className={navLinkClass}>
                    {branchLabels.plural}
                  </NavLink>
                ) : null}
                {Boolean(accessToken && navModuleLinkVisible(PATH.bookings, modulesForNav)) && (
                  <NavLink
                    to={`${PATH.bookings}${suffix}`}
                    className={(props) => `${navLinkClass(props)} inline-flex items-center gap-1`}
                  >
                    <span>Мои записи</span>
                    {unpaidBookingsCount > 0 && (
                      <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow-sm shrink-0">
                        {unpaidBookingsCount}
                      </span>
                    )}
                  </NavLink>
                )}
              </>
            )
          ) : null}
          {accessToken ? (
            <>
              <NavLink to={`${PATH.account}${suffix}`} className={navLinkClass}>
                Личный кабинет
              </NavLink>
              {(!orgSlug || navModuleLinkVisible(PATH.mySubscriptions, modulesForNav)) && (
                <NavLink to={`${PATH.mySubscriptions}${suffix}`} className={navLinkClass}>
                  Мои абонементы
                </NavLink>
              )}
            </>
          ) : null}
        </>
      )}
    </nav>
  );

  if (useOrgHeroBanner && widgetConfig) {
    return (
      <header className="relative z-30 border-b border-[var(--border)]">
        <EmbedOrgHeroHeader config={widgetConfig} />
        <div className="border-t border-[var(--border)]/60 bg-[var(--bg-elevated)]">
          <div className="mx-auto w-full max-w-none px-3 sm:px-5 py-3">
            {headerNav}
          </div>
        </div>
      </header>
    );
  }

  if (useGuestShowcaseHero && guestShowcaseOrg) {
    return (
      <header className="relative z-30 border-b border-[var(--border)]">
        <EmbedGuestShowcaseHeroHeader org={guestShowcaseOrg} />
        <div className="border-t border-[var(--border)]/60 bg-[var(--bg-elevated)]">
          <div className="mx-auto w-full max-w-none px-3 sm:px-5 py-3">
            {headerNav}
          </div>
        </div>
      </header>
    );
  }

  if (clientMiniSite) {
    const heroBanner = widgetConfig ? <EmbedOrgHeroHeader config={widgetConfig} /> : null;
    return (
      <div
        className="relative z-30 shadow-[var(--shadow-card)] bg-[var(--accent-bg)] text-[var(--accent-fg)]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {heroBanner}
        <BlueHeader title={title} subtitle="Клиентский сайт" footer={headerNav} />
      </div>
    );
  }

  return (
    <header
      className="relative z-30 border-b border-[var(--border)] bg-[var(--bg-elevated)] px-0 py-3 sm:px-0"
      style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
    >
      <div className="mx-auto flex w-full max-w-none flex-wrap items-center justify-between gap-3 px-3 sm:px-5">
        <NavLink
          to={isTrainerShell ? `${PATH.trainer}${suffix}` : `${PATH.home}${suffix}`}
          className={
            showHeaderPhoto
              ? 'flex min-w-0 max-w-[min(100%,260px)] shrink items-center gap-2'
              : 'text-sm font-semibold text-[var(--text)]'
          }
        >
          {showHeaderPhoto ? (
            <>
              <span className="sr-only">{isTrainerShell ? `Тренер · ${title}` : title}</span>
              <img
                src={headerPhotoUrl}
                alt=""
                className="max-h-10 w-auto max-w-full object-contain object-left"
                loading="lazy"
                decoding="async"
                onError={() => setHeaderPhotoBroken(true)}
              />
              {isTrainerShell ? (
                <span className="shrink-0 text-xs font-medium text-[var(--text-muted)]" aria-hidden>
                  Тренер
                </span>
              ) : null}
            </>
          ) : (
            <>{isTrainerShell ? `Тренер · ${title}` : title}</>
          )}
        </NavLink>
        {headerNav}
      </div>
    </header>
  );
}

function AppFooter() {
  return <EmbedSiteFooter />;
}

/** Full‑screen overlay when session was valid but got lost (token expired + refresh failed). */
function SessionWatcher() {
  const { me, accessToken, orgSlug } = useEmbedBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const [searchParams] = useSearchParams();
  const [hadSession, setHadSession] = useState(false);

  useEffect(() => {
    if (me) setHadSession(true);
  }, [me]);

  const embedGlobal = searchParams.get('embed_global') === '1';
  const sessionLost = hadSession && !me && !accessToken && !embedGlobal;

  if (!sessionLost) return null;

  const isTrainerRoute = location.pathname.startsWith('/trainer');
  const signInPath = isTrainerRoute ? PATH.trainerSignIn : PATH.signIn;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[var(--bg)] p-6">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg-soft)]">
          <svg className="h-6 w-6 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
          </svg>
        </div>
        <p className="text-base font-semibold text-[var(--text)]">Сессия истекла</p>
        <p className="mt-1 text-sm text-[var(--text-muted)]">Войдите, чтобы продолжить</p>
        <button
          type="button"
          onClick={() => {
            if (orgSlug) clearEmbedSession(orgSlug);
            navigate(`${signInPath}${suffix}`, { replace: true });
          }}
          className="mt-5 h-9 rounded-sm bg-[var(--btn-primary-bg)] px-5 text-sm font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]"
        >
          Войти
        </button>
      </div>
    </div>
  );
}

/** До первого конфига виджета / глобального / витрины — тот же прелоадер, что в админке (BlockLoader). */
function EmbedShellBootstrapGate({ children }: { children: ReactNode }) {
  const { shellBootstrapLoader } = useEmbedBootstrap();
  if (!shellBootstrapLoader) {
    return children;
  }
  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)]">
      <main className="flex flex-1 flex-col items-center justify-center p-6">
        <BlockLoader label="Загрузка…" hole="bg" minHeight={false} className="min-h-[280px] w-full max-w-md" />
      </main>
    </div>
  );
}

function AppLayoutBody() {
  const { widgetContour } = useEmbedBootstrap();

  if (widgetContour === 'mobile') {
    return <MobileShell />;
  }

  if (widgetContour === 'trainer-mobile') {
    return <TrainerMobileShell />;
  }

  if (widgetContour === 'booking') {
    return <BookingShell />;
  }

  return (
    <div
      id={widgetContour === 'client' ? 'domcrm-embed-client-site' : 'domcrm-embed-viewport'}
      className="flex min-h-screen flex-col"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <AppHeader />
      <BrandExpectNotice />
      <main className="relative z-10 w-full min-w-0 max-w-none flex-1 px-3 py-2 sm:px-5 sm:py-3">
        <Outlet />
      </main>
      <AppFooter />
    </div>
  );
}

export function AppLayout() {
  return (
    <EmbedBootstrapProvider>
      <EmbedWidgetTheme />
      <ConfirmDialogProvider>
        <EmbedShellBootstrapGate>
          <AppLayoutBody />
          <SessionWatcher />
        </EmbedShellBootstrapGate>
      </ConfirmDialogProvider>
    </EmbedBootstrapProvider>
  );
}
